import { type FC } from 'react';
import { createFragmentContainer, graphql } from 'react-relay/legacy.js';
import { TRADE, CONSUMER } from 'dibs-buyer-layout/exports/userTypeEnums.ts';
import { ProductTileAd } from 'dibs-google-ad-manager/exports/ProductTileAd.tsx';
import { getProductTileAdMap } from '../../utils/adHelpers.ts';
import { useSbSelector } from '../../reducers/useSbSelector.ts';
import { useServerVarsContext } from '../../global/ServerVarsContext/ServerVarsContext.tsx';

import classnames from 'classnames';
import styles from './SbSharedProductAdWrapper.scss';
import dibsCss from 'dibs-css';

import { type SbSharedProductAdWrapper_itemSearch$data } from './__generated__/SbSharedProductAdWrapper_itemSearch.graphql.ts';

type Props = {
    index: number;
    pageCol?: number;
    itemSearch: SbSharedProductAdWrapper_itemSearch$data;
    isPlaceholder?: boolean;
};

export const SbSharedProductAdWrapperComponent: FC<Props> = ({
    index,
    pageCol,
    itemSearch,
    isPlaceholder = false,
}) => {
    const relayVariables = useSbSelector(state => state.relayVariables.variables);
    const { isDesktop, areDisplayAdsEnabled, isQuickFiltersRedesignVariant } =
        useServerVarsContext();
    // reading isTrade value from relay vars b/c we need to get correct ad config values
    // during first render when google tag script starts loading ads.
    const showTradeAds = relayVariables.isTrade;

    const adMap = getProductTileAdMap({
        uriRef: itemSearch?.displayUriRef || '',
        pageCol,
        pageSize: relayVariables.first ?? 60,
        userType: showTradeAds ? TRADE : CONSUMER,
        deviceType: isDesktop ? 'desktop' : 'mobile',
        areDisplayAdsEnabled,
        isQuickFiltersRedesignVariant,
    });

    const ad = adMap[index + 1]; // 1-indexed

    if (!ad) {
        return null;
    }

    const containerClassName = classnames(dibsCss.minW0, {
        [styles.mobileTileContainer]: !isDesktop,
    });

    if (isPlaceholder) {
        return (
            <div
                className={classnames(styles.shimmer, dibsCss.rounded2px, containerClassName, {
                    [dibsCss.hFull]: isDesktop,
                    [dibsCss.wFull]: isDesktop,
                    [dibsCss.w75]: !isDesktop,
                    [dibsCss.mAuto]: !isDesktop,
                    [dibsCss.h400px]: !isDesktop,
                })}
            />
        );
    }

    return <ProductTileAd {...ad} lazyLoad containerClassName={containerClassName} />;
};

export const SbSharedProductAdWrapper = createFragmentContainer(SbSharedProductAdWrapperComponent, {
    itemSearch: graphql`
        fragment SbSharedProductAdWrapper_itemSearch on ItemSearchQueryConnection {
            displayUriRef
        }
    `,
});
